import React from 'react';

import type { TextStyle } from 'react-native';
import { Text as RNText, StyleSheet } from 'react-native';
import { fontBodyStyles, fontHeadingStyles } from 'ucl/components/text/Typography';

import { ISanityBlockContent } from '@rbi-ctg/menu';
import Link from 'components/link';
import { fontSizes } from 'styles/constants/fonts';
import { useAnchorContext } from 'utils/static-page/hooks/useAnchorContext';

import { SanityBlockContentLinkProps } from './types';

type FontSizeToken = keyof typeof fontSizes;

/**
 * This is a useful override for removing unwanted component hierarchy.
 * E.g. serializers.marks.em by default adds an <em /> wrapper around text content.
 * If you want to only render the container component and text, override
 * the serializers where serializers.marks.em = PassthroughComponent
 */
export const PassthroughComponent: React.FC<React.PropsWithChildren<unknown>> = props => (
  <>{props.children}</>
);

const linkStyles = StyleSheet.create({
  noDecoration: { textDecorationLine: 'none' } as TextStyle,
});

const buildLinkTextStyle = (linkProps: { [key: string]: unknown } | undefined): TextStyle[] => {
  const styles: TextStyle[] = [linkStyles.noDecoration];
  if (!linkProps) {
    return styles;
  }

  const { fontFamily, fontSize, color, fontWeight, textAlign, lineHeight } = linkProps as {
    fontFamily?: 'heading' | 'body' | string;
    fontSize?: number | string;
    color?: string;
    fontWeight?: TextStyle['fontWeight'];
    textAlign?: TextStyle['textAlign'];
    lineHeight?: number | string;
  };

  if (fontWeight !== undefined) {
    styles.push({ fontWeight });
  }
  if (typeof fontSize === 'number') {
    styles.push({ fontSize });
  } else if (typeof fontSize === 'string' && fontSize in fontSizes) {
    styles.push({ fontSize: fontSizes[fontSize as FontSizeToken] });
  }
  if (color) {
    styles.push({ color });
  }
  if (textAlign) {
    styles.push({ textAlign });
  }
  if (fontFamily === 'body') {
    styles.push({ fontFamily: fontBodyStyles.regular.fontFamily });
  } else if (fontFamily === 'heading') {
    styles.push({ fontFamily: fontHeadingStyles.regular.fontFamily });
  } else if (typeof fontFamily === 'string') {
    styles.push({ fontFamily });
  }
  if (typeof lineHeight === 'number') {
    styles.push({ lineHeight });
  }

  return styles;
};

export const FormatSanityLink = ({
  children,
  mark,
  linkProps,
  ...rest
}: SanityBlockContentLinkProps) => {
  const href = mark?.href ?? '';
  const anchorContext = useAnchorContext();
  const isAnchorLink = href.startsWith('#');

  return (
    <Link
      to={isAnchorLink ? undefined : href}
      {...rest}
      testID="sanity-link"
      isInline
      isDisabled={false}
      {...(isAnchorLink
        ? {
            onPress: () => anchorContext?.scrollToTarget(href.replace(/\#/g, ''), true),
          }
        : null)}
    >
      <RNText
        ellipsizeMode="tail"
        numberOfLines={1}
        style={buildLinkTextStyle(linkProps as { [key: string]: unknown } | undefined)}
      >
        {children}
      </RNText>
    </Link>
  );
};

// Used to avoid rendering empty lines
export function sanitizeSanityBlockContent(content: ISanityBlockContent[]) {
  return content?.filter(line => !!line?.children?.[0]?.text?.trim());
}
